
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Plus, Settings, Share, UserPlus } from "lucide-react";
import { useAdditionalProfiles } from "@/hooks/useAdditionalProfiles";
import { useHandoverToken } from "@/hooks/useHandoverToken";
import { CreateProfileModal } from "./CreateProfileModal";
import { ProfileInviteDrawer } from "./ProfileInviteDrawer";
import { ProfileManageDrawer } from "./ProfileManageDrawer";
import { useToast } from "@/hooks/use-toast";
import ButtonCircle from "@/components/ui/ButtonCircle";

interface ProfileManagementInterfaceProps {
  className?: string;
}

export const ProfileManagementInterface = ({ className }: ProfileManagementInterfaceProps) => {
  const { additionalProfiles, getDisplayName, getRelationshipLabel, isLoadingProfiles } = useAdditionalProfiles();
  const { generateHandoverToken, copyTokenToClipboard, isGenerating } = useHandoverToken();
  const { toast } = useToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedProfileForInvite, setSelectedProfileForInvite] = useState<any>(null);
  const [selectedProfileForManage, setSelectedProfileForManage] = useState<any>(null);

  const getInitials = (profile: any) => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return profile.username?.slice(0, 2).toUpperCase() || "?";
  };

  const handleGenerateToken = async (profileId: string) => {
    try {
      const token = await generateHandoverToken(profileId);
      await copyTokenToClipboard(token);
    } catch (error) {
      console.error('Failed to generate handover token:', error);
    }
  };

  const handleInviteClick = (profile: any) => {
    console.log('🔍 ProfileManagementInterface - Invite clicked for profile:', profile);
    console.log('🔍 ProfileManagementInterface - Profile referral_code:', profile.referral_code);
    setSelectedProfileForInvite(profile);
  };

  const handleManageClick = (profile: any) => {
    setSelectedProfileForManage(profile);
  };

  const getRelationshipBadgeVariant = (type: string) => {
    switch (type) {
      case 'gift':
        return 'default';
      case 'memorial':
        return 'secondary';
      case 'managed':
      default:
        return 'outline';
    }
  };

  if (isLoadingProfiles) {
    return (
      <Card className={`uplivion-card ${className}`}>
        <CardHeader>
          <CardTitle>Additional Timelines</CardTitle>
          <CardDescription>Loading your managed timelines...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card className={`uplivion-card ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Additional Timelines
            <ButtonCircle
              onClick={() => setIsCreateModalOpen(true)}
              size="small"
              backgroundColor="bg-uplivion-blue"
              iconColor="text-white"
              aria-label="Create new timeline"
            >
              <Plus />
            </ButtonCircle>
          </CardTitle>
          <CardDescription>
            Manage timelines you've created for others or that have been shared with you
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {additionalProfiles.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">
                You haven't created any additional timelines yet.
              </p>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Timeline
              </Button>
            </div>
          ) : (
            <>
              {additionalProfiles.map((profile) => (
                <div
                  key={profile.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={profile.profile_picture || ""} />
                      <AvatarFallback className="bg-uplivion-blue text-white">
                        {getInitials(profile)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{getDisplayName(profile)}</span>
                        <Badge variant={getRelationshipBadgeVariant(profile.relationship_type || 'managed')}>
                          {getRelationshipLabel(profile.relationship_type || 'managed')}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">@{profile.username}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInviteClick(profile)}
                      className="gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Invite
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateToken(profile.id)}
                      disabled={isGenerating}
                      className="gap-2"
                    >
                      <Share className="h-4 w-4" />
                      {isGenerating ? 'Generating...' : 'Handover'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleManageClick(profile)}
                      className="gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t">
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  variant="outline"
                  className="w-full gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Another Timeline
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <CreateProfileModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {selectedProfileForInvite && (
        <ProfileInviteDrawer
          isOpen={!!selectedProfileForInvite}
          onClose={() => setSelectedProfileForInvite(null)}
          profile={selectedProfileForInvite}
        />
      )}

      {selectedProfileForManage && (
        <ProfileManageDrawer
          isOpen={!!selectedProfileForManage}
          onClose={() => setSelectedProfileForManage(null)}
          profile={selectedProfileForManage}
          onHandoverToken={handleGenerateToken}
        />
      )}
    </>
  );
};
